import React, { useCallback, useMemo, useState } from "react";
import * as R from "ramda";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { Tabs, Badge, Icon, Button, Popconfirm, notification } from "antd";
import { compose, branch, renderComponent } from "recompose";
import { withTranslation } from "react-i18next";
import {
  dispatchOnMount,
  dispatchOnUnmount,
  isLoading
} from "@worcket/common/hocs";

import * as gtag from "../../../../lib/gtag";
import CandidateModal from "../../../../components/CandidateModal";
import {
  FETCH_MATCH_DETAILS,
  fetchCandidateDetails,
  moveCandidateToProcess,
  RESET_SELECTED_CANDIDATE,
  sendScreeningReminder,
  requestCvUpdate,
  shareCandidate,
  fetchMatchMessages
} from "../../../../store/actions/candidates";
import { markCandidatesAsRead } from "../../../../store/actions/opportunity";
import useEmailInvite from "../../../../hooks/custom/useEmailInvite";
import { CANDIDATE_LISTS } from "../../../../constants/opportunity";
import {
  canAdvanceCandidateToProcess,
  getCandidateScreeningStatus,
  getCandidatesListActions,
  getMoveToSelectedBlockReason,
  getScreeningTag,
  isRejectedScreeningStatus,
  normalizeCandidateScreeningTags
} from "../../../../utils/candidates";
import { moveCandidatesToProcessConfirm } from "../../../../utils/batchActions";
import {
  WrapperAction,
  Title
} from "../../../../components/CandidateModal/WrapperAction";
import Messages from "../../../../components/Chat/Messages";

import ShareCandidatesModal from "./ShareCandidatesModal";

const Chat = styled.div`
  display: flex;
  height: 400px;
  justify-content: center;

  @media (max-width: 767px) {
    min-height: 0;
  }
`;

const CandidateDetails = ({
  t,
  history,
  candidate,
  listCandidate = {},
  loading,
  location,
  updateCandidateStatus,
  canMove,
  canChat,
  restrictAdvanceToProcessByScreening,
  closeModal,
  company,
  opportunity,
  list,
  moveCandidateToProcess,
  sendScreeningReminder,
  sendScreeningReminderStatus,
  requestCvUpdate,
  requestCvUpdateStatus,
  shareCandidate,
  markCandidatesAsRead,
  onNext,
  onPrev,
  onSelect,
  isSelected,
  interaction,
  unreadMessages,
  isRegistered,
  selectedCandidates = [],
  selectedCandidatesLoading = false
}) => {
  const [inviteCandidate, inviteCandidateStatus] = useEmailInvite(
    opportunity.idCompany,
    opportunity.id
  );

  const [shareCandidateModalVisible, setShareCandidateModalVisible] = useState(
    false
  );

  const candidateForDisplay = useMemo(
    () => ({
      ...listCandidate,
      ...candidate,
      previousMatch: candidate.previousMatch || listCandidate.previousMatch
    }),
    [candidate, listCandidate]
  );

  const candidateScreeningStatus = useMemo(
    () => getCandidateScreeningStatus(candidateForDisplay, opportunity),
    [candidateForDisplay, opportunity]
  );

  const candidateForActions = useMemo(
    () => ({
      ...candidateForDisplay,
      answers: undefined,
      screeningStatus: candidateScreeningStatus
    }),
    [candidateForDisplay, candidateScreeningStatus]
  );

  const moveToSelectedBlockReason = useMemo(
    () =>
      getMoveToSelectedBlockReason({
        candidate: candidateForDisplay,
        selectedCandidates,
        selectedCandidatesLoading
      }),
    [candidateForDisplay, selectedCandidates, selectedCandidatesLoading]
  );

  const blockedMoveToSelectedMessage = useMemo(
    () =>
      moveToSelectedBlockReason === "loading"
        ? t("components:CandidateCard.selectedCandidatesLoading")
        : t(
            `components:CandidateCard.${
              moveToSelectedBlockReason === "process"
                ? "previousMatch"
                : "alreadySelected"
            }`
          ),
    [moveToSelectedBlockReason, t]
  );

  const {
    showMoveToProcess,
    showMoveToSelected,
    showScreeningEmail,
    showInviteToWorcket,
    showRejected
  } = getCandidatesListActions(candidateForActions, list, opportunity);

  const normalizedScreeningTags = useMemo(
    () => normalizeCandidateScreeningTags(candidateForDisplay),
    [candidateForDisplay]
  );

  const headerTags = useMemo(() => {
    const tags = [...normalizedScreeningTags];

    if (candidateForDisplay.previousMatch) {
      tags.push(t("components:CandidateCard.previousMatch"));
    }

    if (
      list !== CANDIDATE_LISTS.selected &&
      moveToSelectedBlockReason === "selected"
    ) {
      tags.push(t("components:CandidateCard.alreadySelected"));
    }

    if (
      list === CANDIDATE_LISTS.selected &&
      candidateForDisplay.candidateLikeOpportunity
    ) {
      tags.push(t("components:CandidateCard.acceptedOpportunity"));
    }

    return tags;
  }, [
    candidateForDisplay.candidateLikeOpportunity,
    moveToSelectedBlockReason,
    candidateForDisplay.previousMatch,
    list,
    normalizedScreeningTags,
    t
  ]);

  const modalScreening = useMemo(() => {
    if (
      list === CANDIDATE_LISTS.selected &&
      !candidateForDisplay.candidateLikeOpportunity
    ) {
      return {
        screeningText: t("components:CandidateCard.invitationSent")
      };
    }

    if (!candidateScreeningStatus) {
      return undefined;
    }

    return getScreeningTag(t, candidateScreeningStatus);
  }, [candidateForDisplay, candidateScreeningStatus, list, t]);

  const moveCandidateToSelected = useCallback(() => {
    if (moveToSelectedBlockReason) {
      notification.info({
        message: t("global:alert"),
        description: blockedMoveToSelectedMessage
      });
      return;
    }

    updateCandidateStatus({
      candidate: candidateForDisplay,
      status: "accepted",
      message: t("global:good"),
      list,
      opportunity,
      description: t(
        `toasts:candidates.${
          showMoveToProcess ? "movedToProcess" : "movedToSelected"
        }`,
        {
          fullname: R.or(
            R.prop("fullname", candidate),
            `${R.prop("first", candidate)} ${R.prop("last", candidate)}`
          )
        }
      )
    });
    closeModal();
  }, [
    blockedMoveToSelectedMessage,
    candidateForDisplay,
    moveToSelectedBlockReason,
    closeModal,
    list,
    showMoveToProcess,
    opportunity,
    t,
    updateCandidateStatus,
    candidate
  ]);

  const handleExplicitMoveToProcess = useCallback(() => {
    moveCandidateToProcess({
      candidate: candidateForDisplay,
      list,
      opportunity,
      message: t("global:good"),
      description: t("toasts:candidates.movedToProcess", {
        fullname: R.or(
          R.prop("fullname", candidate),
          `${R.prop("first", candidate)} ${R.prop("last", candidate)}`
        )
      })
    });
    closeModal();
  }, [
    candidate,
    candidateForDisplay,
    closeModal,
    list,
    moveCandidateToProcess,
    opportunity,
    t
  ]);

  const handleMoveToProcess = useCallback(() => {
    if (
      restrictAdvanceToProcessByScreening &&
      !canAdvanceCandidateToProcess(candidateForDisplay, opportunity)
    ) {
      notification.warning({
        message: t("global:alert"),
        description: t("toasts:candidates.advanceToProcessScreeningBlocked")
      });
      return;
    }

    const needsConfirmation =
      [CANDIDATE_LISTS.selected, CANDIDATE_LISTS.suggested].includes(list) &&
      (candidateScreeningStatus === "pending" ||
        isRejectedScreeningStatus(candidateScreeningStatus));

    if (!needsConfirmation) return handleExplicitMoveToProcess();

    return moveCandidatesToProcessConfirm({
      plurals: false,
      pluralsPending: false,
      pluralsRejected: false,
      pendingCount: candidateScreeningStatus === "pending" ? 1 : 0,
      rejectedCount: isRejectedScreeningStatus(candidateScreeningStatus)
        ? 1
        : 0,
      selectedCount: 1,
      onOk: handleExplicitMoveToProcess
    });
  }, [
    candidateForDisplay,
    candidateScreeningStatus,
    handleExplicitMoveToProcess,
    list,
    opportunity,
    restrictAdvanceToProcessByScreening,
    t
  ]);

  const handleCandidateShare = useCallback(
    opportunities =>
      shareCandidate({
        candidate,
        opportunities
      }),
    [candidate, shareCandidate]
  );

  const allChatbotTags = useMemo(
    () => [
      R.assoc("chatbotTags", [...normalizedScreeningTags].sort(), {
        title: t("opportunity:screening.title")
      })
    ],
    [normalizedScreeningTags, t]
  );

  const read = useSelector(
    R.pipe(
      R.pathOr([], ["opportunity", "selected", "views"]),
      R.includes(candidate.id)
    )
  );

  return (
    <>
      <ShareCandidatesModal
        onOk={handleCandidateShare}
        onClose={() => setShareCandidateModalVisible(false)}
        visible={shareCandidateModalVisible}
      />
      <CandidateModal
        list={list}
        isSelected={isSelected}
        onSelect={onSelect}
        loading={loading}
        onClose={closeModal}
        actions={[
          <WrapperAction colorHover="primary">
            <Button
              icon={read ? "eye-invisible" : "eye"}
              shape="circle"
              onClick={() => {
                gtag.event({
                  category: "VacancyCandidates",
                  action: `${list}.${
                    isRegistered ? "Registered" : "External"
                  }.mark-as-read`,
                  label: opportunity.id
                });
                markCandidatesAsRead({
                  id: opportunity.id,
                  candidates: [candidate.id],
                  read: !read
                });
              }}
              style={{ margin: "0 auto" }}
            />
            <Title>
              {t(
                read
                  ? "components:CandidateCard.markAsUnread"
                  : "components:CandidateCard.markAsRead"
              )}
            </Title>
          </WrapperAction>,
          showInviteToWorcket && (
            <WrapperAction colorHover="primary">
              <Button
                data-test="invite-to-worcket-btn"
                key="accepted"
                icon="mail"
                shape="circle"
                loading={inviteCandidateStatus === "loading"}
                disabled={inviteCandidateStatus === "resolved"}
                onClick={() => {
                  gtag.event({
                    category: "VacancyCandidates",
                    action: `${list}.${
                      isRegistered ? "Registered" : "External"
                    }.invite-to-worcket`,
                    label: opportunity.id
                  });
                  inviteCandidate(candidate.id);
                }}
                style={{ margin: "0 auto" }}
              />
              <Title>{t("components:CandidateCard.inviteToWorcket")}</Title>
            </WrapperAction>
          ),
          showScreeningEmail && (
            <WrapperAction colorHover="primary">
              <Button
                data-test="send-screening-email-btn"
                icon="mail"
                shape="circle"
                onClick={() => {
                  gtag.event({
                    category: "VacancyCandidates",
                    action: `${list}.${
                      isRegistered ? "Registered" : "External"
                    }.send-email-reminder`,
                    label: opportunity.id
                  });
                  sendScreeningReminder(candidate.id);
                }}
                loading={sendScreeningReminderStatus === "loading"}
                disabled={sendScreeningReminderStatus === "resolved"}
                style={{ margin: "0 auto" }}
              />
              <Title>{t("components:CandidateCard.sendScreeningEmail")}</Title>
            </WrapperAction>
          ),
          showMoveToProcess && canMove && interaction !== "process" && (
            <WrapperAction colorHover="primary">
              <Button
                data-test="move-to-process-btn"
                type="primary"
                icon="swap"
                onClick={() => {
                  gtag.event({
                    category: "VacancyCandidates",
                    action: `${list}.${
                      isRegistered ? "Registered" : "External"
                    }.move-to-process`,
                    label: opportunity.id
                  });
                  handleMoveToProcess();
                }}
                shape="circle"
                style={{ margin: "0 auto" }}
              />
              <Title>{t("components:CandidateCard.moveToProcess")}</Title>
            </WrapperAction>
          ),
          showMoveToSelected && interaction !== "selected" && (
            <WrapperAction colorHover="primary">
              <Button
                data-test="move-to-selected-btn"
                type="primary"
                icon={"check-circle"}
                shape="circle"
                disabled={moveToSelectedBlockReason === "loading"}
                title={
                  moveToSelectedBlockReason ? blockedMoveToSelectedMessage : undefined
                }
                onClick={() => {
                  gtag.event({
                    category: "VacancyCandidates",
                    action: `${list}.${
                      isRegistered ? "Registered" : "External"
                    }.move-to-selected`,
                    label: opportunity.id
                  });
                  moveCandidateToSelected();
                }}
              />
              <Title>{t("components:CandidateCard.moveToSelected")}</Title>
            </WrapperAction>
          ),
          showRejected && canMove && interaction !== "rejected" && (
            <WrapperAction colorHover="orange">
              <Popconfirm
                key="rejectedPopconfirm"
                placement="topLeft"
                title={t("opportunity:candidates.rejectConfirm")}
                onConfirm={() => {
                  gtag.event({
                    category: "VacancyCandidates",
                    action: `${list}.${
                      isRegistered ? "Registered" : "External"
                    }.reject`,
                    label: opportunity.id
                  });
                  updateCandidateStatus({
                    candidate: candidateForDisplay,
                    status: "rejected",
                    list,
                    opportunity,
                    message: t("global:good"),
                    description: t("toasts:candidates.rejectedCandidate", {
                      fullname: R.or(
                        R.prop("fullname", candidate),
                        `${R.prop("first", candidate)} ${R.prop(
                          "last",
                          candidate
                        )}`
                      )
                    })
                  });
                  closeModal();
                }}
                okText={t("global:accept")}
                cancelText={t("global:cancel")}
              >
                <Button
                  data-test="reject-candidate-btn"
                  key="rejected"
                  type="primary"
                  icon="minus-circle"
                  shape="circle"
                />
              </Popconfirm>
              <Title> {t("global:reject")}</Title>
            </WrapperAction>
          ),
          <WrapperAction colorHover="primary">
            <Button
              onClick={() => setShareCandidateModalVisible(true)}
              data-test="share-candidate-btn"
              key="share"
              type="primary"
              icon="export"
              shape="circle"
            />
            <Title>
              {t(
                "opportunity:candidates.multipleSelection.shareCandidateToOpp"
              )}
            </Title>
          </WrapperAction>
        ]}
        chatTab={
          canChat && (
            <Tabs.TabPane
              disabled={!isRegistered}
              tab={
                <div data-test="candidate-modal-chat-tab">
                  <Badge dot={true} count={unreadMessages} offset={[-9, 1]}>
                    <Icon type="message" />
                  </Badge>
                  <span>{t("opportunity:process.candidateDetails.chat")}</span>
                </div>
              }
              key={`chat`}
              activeKey={`chat`}
            >
              <Chat>
                <Messages
                  key={candidate.id}
                  candidate={candidate}
                  disabled={true}
                />
              </Chat>
            </Tabs.TabPane>
          )
        }
        videoMetadata={candidate.videoMetadata}
        candidate={candidate}
        location={location}
        history={history}
        company={company}
        tags={headerTags}
        chatbotTags={allChatbotTags}
        screening={isRegistered && modalScreening}
        showRequestCvUpdateButton={Boolean(
          opportunity.id && candidate.id && isRegistered
        )}
        requestCvUpdateStatus={requestCvUpdateStatus}
        onRequestCvUpdate={() => {
          gtag.event({
            category: "VacancyCandidates",
            action: `${list}.${
              isRegistered ? "Registered" : "External"
            }.request-cv-update`,
            label: opportunity.id
          });
          requestCvUpdate(candidate.id);
        }}
        onNext={onNext}
        onPrev={onPrev}
      />
    </>
  );
};

const enhancer = compose(
  withRouter,
  withTranslation(["opportunity", "toasts", "form", "global"]),
  connect(
    (state, ownProps) => {
      const selectedCandidate = R.defaultTo({}, state.candidates.selected);
      const listCandidate = R.pipe(
        R.pathOr([], ["candidates", "lists", ownProps.list]),
        R.find(R.propEq("id", ownProps.candidateId)),
        R.defaultTo({})
      )(state);
      const candidate = {
        ...listCandidate,
        ...selectedCandidate,
        previousMatch:
          selectedCandidate.previousMatch || listCandidate.previousMatch
      };
      const opportunity = R.defaultTo({}, state.opportunity.selected);
      return {
        candidate,
        opportunity,
        isUrlInvalid: FETCH_MATCH_DETAILS.getStatus(state) === "rejected",
        selectedCandidates: R.pathOr(
          [],
          ["candidates", "lists", CANDIDATE_LISTS.selected, "candidates"],
          state
        ),
        selectedCandidatesLoading:
          R.pathOr(
            true,
            ["candidates", "lists", CANDIDATE_LISTS.selected, "loading"],
            state
          ) ||
          R.pathOr(
            false,
            ["candidates", "lists", CANDIDATE_LISTS.selected, "rejected"],
            state
          ),
        sendScreeningReminderStatus: R.propOr(
          "initial",
          "sendScreeningReminderStatus",
          candidate
        ),
        requestCvUpdateStatus: R.propOr(
          "initial",
          "requestCvUpdateStatus",
          candidate
        ),
        unreadMessages: R.pipe(
          R.pathOr([], ["messages", "newMessages"]),
          R.filter(
            R.both(
              R.propEq("idCandidate", candidate.id),
              R.propEq("idOpportunity", opportunity.id)
            )
          ),
          R.length
        )(state)
      };
    },
    {
      resetSelectedCandidate: RESET_SELECTED_CANDIDATE.run,
      moveCandidateToProcess,
      sendScreeningReminder,
      requestCvUpdate,
      shareCandidate,
      markCandidatesAsRead
    }
  ),
  dispatchOnMount(({ candidateId }) => fetchCandidateDetails(candidateId)),
  dispatchOnMount(({ candidateId, isRegistered }) =>
    isRegistered ? fetchMatchMessages(candidateId) : function() {}
  ),
  dispatchOnMount(({ candidateId, opportunityId }) =>
    markCandidatesAsRead({
      id: opportunityId,
      candidates: [candidateId],
      read: true
    })
  ),
  dispatchOnUnmount(RESET_SELECTED_CANDIDATE.run),
  branch(
    ({ isUrlInvalid }) => isUrlInvalid,
    renderComponent(({ t, closeModal }) => {
      notification.error({
        message: t("global:error"),
        description: t("toasts:candidates.loadMatchDetailsRejected")
      });
      closeModal();
      return null;
    })
  ),
  isLoading(FETCH_MATCH_DETAILS)
);

export default enhancer(CandidateDetails);
